import React, { useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { getLanguageService } from 'vscode-json-languageservice';
/*tag*/
export default function JsonEditor({
  jsonValue = `{
  "user_profile": {
    "type": "instagram",
    "username": "photools.in",
    "is_business": true
  }
}`,
  onJsonChange = () => {},
  onError = () => {},
  isDarkMode = false,
}) {
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const isFormattingRef = useRef(false);
  const debounceTimerRef = useRef(null);

  const fixBackslashes = (text) => {
    try {
      // Only re-stringify if the JSON is valid but might have formatting issues
      // Don't modify valid JSON with escaped quotes
      const parsed = JSON.parse(text);
      // Only re-stringify if the original text is not already properly formatted
      const stringified = JSON.stringify(parsed);
      if (text.trim() !== stringified && text.trim() !== JSON.stringify(parsed, null, 2)) {
        return stringified;
      }
    } catch (err) {
      console.log('err', err);
    }
    return text;
  };

  const updateEditorContent = (newValue) => {
    if (!editorRef.current) return;
    const editor = editorRef.current;
    const model = editor.getModel();
    if (!model) return;

    if (model.getValue() === newValue) return;

    const position = editor.getPosition();
    isFormattingRef.current = true;

    editor.executeEdits('formatter', [
      {
        range: model.getFullModelRange(),
        text: newValue,
        forceMoveMarkers: true,
      },
    ]);

    if (position) {
      editor.setPosition(position);
    }
    isFormattingRef.current = false;
  };

  // Run once Monaco is ready
  function handleEditorDidMount(editor, monaco) {
    monacoRef.current = monaco;
    editorRef.current = editor;

    // JSON Language Service setup
    const jsonService = getLanguageService({
      schemaRequestService: async () => '{}',
    });

    editor.onDidChangeModelContent(async () => {
      if (isFormattingRef.current) return;

      const model = editor.getModel();
      if (!model) return;

      const text = model.getValue();

      // Validation logic should stay relatively fast
      const document = {
        uri: model.uri.toString(),
        languageId: 'json',
        version: 1,
        getText: () => text,
      };

      try {
        const jsonDoc = jsonService.parseJSONDocument(document);
        const diagnostics = await jsonService.doValidation(document, jsonDoc);
        monaco.editor.setModelMarkers(
          model,
          'json',
          diagnostics.map((d) => ({
            severity: monaco.MarkerSeverity.Error,
            message: d.message,
            startLineNumber: d.range.start.line + 1,
            startColumn: d.range.start.character + 1,
            endLineNumber: d.range.end.line + 1,
            endColumn: d.range.end.character + 1,
          })),
        );

        if (diagnostics.length > 0) {
          onError(diagnostics[0].message);
        } else {
          onError('');
        }

        // Debounce formatting and parent update
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(async () => {
          const currentText = model.getValue();
          const fixedText = fixBackslashes(currentText);

          if (diagnostics.length === 0 && currentText.trim()) {
            try {
              const parsed = JSON.parse(fixedText);
              const formatted = JSON.stringify(parsed, null, 2);

              if (currentText !== formatted) {
                updateEditorContent(formatted);
              }
              onJsonChange(formatted);
            } catch (parseErr) {
              // If parsing fails after some delay, just notify parent
              onJsonChange(currentText);
            }
          } else {
            onJsonChange(currentText);
          }
        }, 1000); // 1 second debounce
      } catch (err) {
        console.error('Validation error:', err);
        onError(`Validation error: ${err.message}`);
      }
    });
  }

  // Update editor value when jsonValue prop changes
  useEffect(() => {
    if (editorRef.current && jsonValue !== editorRef.current.getValue()) {
      const fixedValue = fixBackslashes(jsonValue);
      if (fixedValue !== editorRef.current.getValue()) {
        updateEditorContent(fixedValue);
      }
    }
  }, [jsonValue]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Editor
      height="100%"
      defaultLanguage="json"
      value={jsonValue}
      theme={isDarkMode ? 'vs-dark' : 'vs-light'}
      onMount={handleEditorDidMount}
      options={{
        minimap: { enabled: false },
        fontSize: 14,
        automaticLayout: true,
        wordWrap: 'on',
      }}
    />
  );
}
